import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useMutation } from '@sequencehq/api'
import type { Currency } from '@sequencehq/api'
import {
  Box,
  GreyGrey70,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  SimpleModal,
  TextInputField,
  useNotifications
} from '@sequencehq/core-components'
import { useState, type ReactElement } from 'react'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { extractApiErrorMessage } from '../../common/extractApiErrorMessage'
import { WATERFALL_EXPORT_COPY } from './waterfallExport.constants'
import { splitCsv } from '@sequencehq/utils'
import type { RevenueWaterfallQueryParams } from './RevenueWaterfallTable/types'

// Xero ledger account codes are entered as a short numeric code (e.g. 201).
const XERO_LEDGER_ACCOUNT_CODE_MAX_LENGTH = 3

type Props = {
  trigger: ReactElement
  filters: RevenueWaterfallQueryParams
}

export const ExportWaterfallReportModal = ({ trigger, filters }: Props) => {
  const flags = useFeatureFlags()
  const { displayNotification } = useNotifications()
  const [xeroLedgerAccountCode, setXeroLedgerAccountCode] = useState('')

  const postWaterfallReportMutation = useMutation(
    dashboardv99990101Client.postWaterfallReport
  )

  const onSubmit = async () => {
    const trimmedCode = xeroLedgerAccountCode.trim()
    // Page filters store multi-select ids as one comma-separated string; the
    // POST body expects them as id arrays.
    const customerIds = splitCsv(filters.customerId)
    const productIds = splitCsv(filters.productId)
    try {
      await postWaterfallReportMutation.mutateAsync({
        currency: filters.currency as Currency,
        bookedFrom: filters.bookedFrom,
        bookedTo: filters.bookedTo,
        recognizedUpTo: filters.recognizedUpTo,
        groupBy: filters.groupBy,
        subGroupBy: filters.subGroupBy,
        ...(customerIds.length > 0 ? { customerIds } : {}),
        ...(productIds.length > 0 ? { productIds } : {}),
        ...(trimmedCode !== '' ? { xeroLedgerAccountCode: trimmedCode } : {})
      })
      displayNotification(WATERFALL_EXPORT_COPY.notifications.success, {
        type: 'success',
        truncate: false
      })
      return true
    } catch (e) {
      displayNotification(
        extractApiErrorMessage(e, WATERFALL_EXPORT_COPY.notifications.error),
        { type: 'error' }
      )
      return false
    }
  }

  return (
    <SimpleModal
      minHeight="0"
      variant="dialog"
      title={WATERFALL_EXPORT_COPY.modal.title}
      trigger={trigger}
      cancelButtonText={WATERFALL_EXPORT_COPY.modal.cancelLabel}
      submitButtonText={WATERFALL_EXPORT_COPY.modal.submitLabel}
      submittingButtonText={WATERFALL_EXPORT_COPY.modal.submitLabel}
      data-testid="revrec.waterfall.exportCsv.modal"
      onSubmit={onSubmit}
      onClose={() => setXeroLedgerAccountCode('')}
    >
      <Box {...Primary13Regular} color={GreyGrey70}>
        {WATERFALL_EXPORT_COPY.modal.body}{' '}
        <Link
          to={WATERFALL_EXPORT_COPY.docs.href}
          target="_blank"
          rel="noopener noreferrer"
          color={IndigoIndigo60}
        >
          {WATERFALL_EXPORT_COPY.docs.label}
        </Link>
      </Box>
      {flags.useXeroCodeWaterfallExport && (
        <Box paddingTop="16px">
          <TextInputField
            label="Xero ledger account code"
            description={
              <Box {...Primary13Regular} color={GreyGrey70}>
                Optionally scope the report to a single Xero ledger account
                code. Leave blank to include all codes.
              </Box>
            }
            value={xeroLedgerAccountCode}
            maxLength={XERO_LEDGER_ACCOUNT_CODE_MAX_LENGTH}
            placeholder="e.g. 201"
            onChange={e => setXeroLedgerAccountCode(e.target.value)}
            data-testid="revrec.waterfall.exportCsv.modal.xeroLedgerAccountCode"
          />
        </Box>
      )}
    </SimpleModal>
  )
}
